// UX-Vorschlag P5 („Inbox mit Kontext"): reine Logik — Titel mit Aufgabenbezug,
// Auszug, Eigen-Pings ausblenden, Gruppierung nach Aufgabe. Die Kontextfelder
// sind ADDITIV am bestehenden GET /api/inbox (Vertrag mit "api"); fehlen sie
// (aelterer Server), faellt alles auf den bisherigen Titel zurueck.

import type { InboxItem } from "./api";

export interface InboxItemCtx extends InboxItem {
  /** notification.type bzw. sinngemaess fuer abgeleitete Quellen ("mention", "assignment" …). */
  kind?: string | null;
  /** Notification-Body bzw. Kommentar-Auszug (Klartext). */
  body?: string | null;
  task_title?: string | null;
  project_name?: string | null;
  personal_task_id?: number | null;
  actor_id?: number | null;
  actor_name?: string | null;
  /** Ausloeser == Empfaenger (eigene Handlung). */
  self_ping?: boolean;
  /** Server-Gruppierungsschluessel ("task:<id>" …); fehlt er, bildet der Client einen. */
  group_key?: string;
  /** Antwort per POST …/reply moeglich. */
  can_reply?: boolean;
  update_id?: number | null;
}

/** Klartext-Auszug: Mehrfach-Leerraum zusammenziehen, auf `max` Zeichen kuerzen. */
export function excerpt(body: string | null | undefined, max = 160): string | null {
  if (!body) return null;
  const flat = body.replace(/\s+/g, " ").trim();
  if (flat === "") return null;
  return flat.length > max ? `${flat.slice(0, max - 1).trimEnd()}…` : flat;
}

/**
 * Titel mit Kontext. Heute heisst eine Erwaehnung nur „X hat dich erwähnt" —
 * mit Aufgabentitel wird daraus „X hat dich in „Aufgabe" erwähnt". Steht der
 * Aufgabentitel schon im Servertitel, bleibt der Titel wie er ist.
 */
export function contextTitle(item: InboxItemCtx): string {
  const task = item.task_title?.trim();
  if (!task || item.title.includes(task)) return item.title;
  if (/hat dich erwähnt\.?$/.test(item.title)) {
    return item.title.replace(/hat dich erwähnt\.?$/, `hat dich in „${task}" erwähnt`);
  }
  return `${item.title} · „${task}"`;
}

/** Eigene Handlungen (z. B. Selbst-Zuweisung) ausblenden, ausser auf Wunsch. */
export function visibleInboxItems<T extends InboxItemCtx>(items: T[], showSelf: boolean): T[] {
  return showSelf ? items : items.filter((i) => i.self_ping !== true);
}

export function countSelfPings(items: InboxItemCtx[]): number {
  return items.filter((i) => i.self_ping === true).length;
}

export interface InboxGroup<T extends InboxItemCtx = InboxItemCtx> {
  key: string;
  /** Aufgabentitel, sonst der Titel des neuesten Eintrags. */
  label: string;
  project_name: string | null;
  task_id: number | null;
  personal_task_id: number | null;
  project_id: number | null;
  link: string | null;
  items: T[];
  /** Neuester Zeitstempel der Gruppe (ISO). */
  latest: string;
}

function groupKey(i: InboxItemCtx): string {
  if (i.group_key) return i.group_key;
  if (i.task_id !== null) return `task-${i.task_id}`;
  if (i.personal_task_id != null) return `personal-${i.personal_task_id}`;
  return `single-${i.source_type}-${i.source_id}`;
}

/**
 * Eintraege zur selben Aufgabe zusammenfassen. Gruppen sind nach ihrem
 * neuesten Eintrag sortiert, innerhalb einer Gruppe neueste zuerst.
 */
export function groupInboxByTask<T extends InboxItemCtx>(items: T[]): InboxGroup<T>[] {
  const map = new Map<string, T[]>();
  for (const i of items) {
    const k = groupKey(i);
    const bucket = map.get(k);
    if (bucket) bucket.push(i);
    else map.set(k, [i]);
  }
  const ts = (iso: string): number => {
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? 0 : t;
  };
  const groups: InboxGroup<T>[] = [];
  for (const [key, bucket] of map) {
    bucket.sort((a, b) => ts(b.created_at) - ts(a.created_at));
    const head = bucket[0]!;
    groups.push({
      key,
      label: head.task_title?.trim() || contextTitle(head),
      project_name: head.project_name ?? null,
      task_id: head.task_id,
      personal_task_id: head.personal_task_id ?? null,
      project_id: head.project_id,
      link: head.link,
      items: bucket,
      latest: head.created_at,
    });
  }
  groups.sort((a, b) => ts(b.latest) - ts(a.latest));
  return groups;
}
